import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  TemplateRef,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { WarningIconComponent, ClockIconComponent } from '@kuiper/icons';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { PermissionsDirective } from '../../../../../../ui-components/directives/permissions/permissions.directive';
import { MyInboxService } from '../../../../../common/services/my-inbox/my-inbox.service';
import { RequestService } from '../../../../../common/services/request/request.service';
import { AvatarComponent } from '../../../../../../ui-components/lib/avatar/avatar.component';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { UserDropdownViewComponent } from '../../../../../../ui-components/lib/user-dropdown-view/user-dropdown-view.component';
import { ActionListComponent } from '../../../../../common/components/action-list/action-list.component';
import { RequestCreatorComponent } from '../../../../../common/components/request-creator/request-creator.component';
import { ArchiveConfirmationPopupComponent } from '../../../../../common/components/archive-confirmation-popup/archive-confirmation-popup.component';
import { PermissionsService } from '../../../../../common/services/permissions/permissions.service';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { ExportService } from '../../../../../../ui-components/services/export/export.service';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import { TrimTooltipDirective } from '../../../../../../ui-components/directives/trim-tooltip/trim-tooltip.directive';
import { SlaDirective } from '../../../../../common/directives/sla/sla.directive';
import { StepStatusPipe } from '../../../../../common/pipes/step-status/step-status.pipe';
import { TruncatePipe } from '../../../../../../ui-components/pipes/truncate/truncate.pipe';
import { AppPermissions } from '../../../../../common/constants/app-permissions';
import { RequestHistory } from '../../../../../common/models/request-history.model';
import { FilterOption } from '../../../../../../ui-components/lib/filter-dropdown/filter-dropdown.model';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../../ui-components/lib/table/table.model';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { RequestTypeEnum } from '../../../../../common/enums/request-type';
import { StepStatusEnum } from '../../../../../common/enums/step-status';
import { ActionList } from '../../../../../common/models/action-list.model';
import {
  ApiResponse,
  Meta,
} from '../../../../../common/models/api-response.model';
import { EMPLOYEE_ROUTES } from '../../../../../common/constants/employee.routing.constants';
import {
  filterOptions as FILTER_OPTIONS,
  HISTORY_ARCHIVE_REQUEST_TYPE,
  HISTORY_COLUMNS_CONFIG,
  HISTORY_ID_COLUMN_INDEX,
  HISTORY_FILTER_MAPPING,
  HISTORY_TABLE_FILTER_TABS,
} from './history-tab.constants';
import { TableFilterTabKey } from '../../../../../common/enums/table-filter-tab-key';
import { FilteredTableComponent } from '../../../../../common/components/filtered-table/filtered-table.component';
import { FilteredTablePayload } from '../../../../../common/components/filtered-table/filtered-table.model';
@Component({
  selector: 'lib-history-tab',
  imports: [
    DatePipe,
    BadgeComponent,
    StepStatusPipe,
    SlaDirective,
    TranslocoPipe,
    TruncatePipe,
    AvatarComponent,
    UserDropdownViewComponent,
    ActionListComponent,
    PermissionsDirective,
    TrimTooltipDirective,
    WarningIconComponent,
    ClockIconComponent,
    ArchiveConfirmationPopupComponent,
    RequestCreatorComponent,
    RouterLink,
    TenantPathPipe,
    FilteredTableComponent,
  ],
  templateUrl: './history-tab.component.html',
  styleUrl: './history-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class HistoryTabComponent implements OnInit {
  readonly EMPLOYEE_ROUTES = EMPLOYEE_ROUTES;

  readonly modalService = inject(ModalServiceService);
  requestService = inject(RequestService);
  myInboxService = inject(MyInboxService);
  exportService = inject(ExportService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  parallelStepStatus = StepStatusEnum;
  transloco = inject(TranslocoService);
  permissionsService = inject(PermissionsService);
  toasterService = inject(ToasterService);
  appPermissions = AppPermissions;
  tenantService = inject(TenantService);
  private readonly destroyRef = inject(DestroyRef);
  /*********** modal *********/
  archiveModal = viewChild<TemplateRef<unknown>>('archive');
  restoreModal = viewChild<TemplateRef<unknown>>('restore');

  permissions = input.required<any>();
  workflowName = input.required<string>();
  additionalFilters = input<Record<string, string>>({});

  selectedRequestId = signal<number>(0);
  isLoading = signal<boolean>(false);
  requestId = signal<number>(0);
  historyData = signal<RequestHistory[]>([]);
  meta = signal<Meta | undefined>(undefined);

  columns: TableColumns[] = HISTORY_COLUMNS_CONFIG.map((col, index) => ({
    ...col,
    header: this.transloco.translate(col.header),
    ...(index === HISTORY_ID_COLUMN_INDEX && {
      showViewIcon: (row: any) =>
        this.permissionsService.checkPermission(
          [this.appPermissions.workflow.view],
          row.permissions
        ),
      viewIconRouterLink: (row: RequestHistory) => [
        this.tenantService.tenantPath(
          row.sub_flows_exists
            ? `${EMPLOYEE_ROUTES.REQUESTS.SEQUENTIAL_REQUEST}/${RequestTypeEnum.VIEW}/${row.id}`
            : `${EMPLOYEE_ROUTES.REQUESTS.REQUEST}/${RequestTypeEnum.VIEW}/${row.id}`
        ),
      ],
    }),
  }));

  readonly tabKeys = TableFilterTabKey;

  tableFilterTabs: TableFilterTab[] = HISTORY_TABLE_FILTER_TABS.map(tab => ({
    ...tab,
    label: this.transloco.translate(tab.label),
  }));

  activeTableFilterTab = computed(
    () => this.payload().tab ?? this.tableFilterTabs[0]
  );

  payload = signal<FilteredTablePayload>({
    page: 1,
    size: 25,
    sortColumn: { key: '', value: 'asc' },
    search: '',
    filters: {},
    tab: this.tableFilterTabs[0],
  });

  private readonly viewDetailsAction: ActionList = {
    key: 'view_details',
    hasIcon: true,
    label: this.transloco.translate('view_details_action_item'),
    hasDivider: false,
    labelCustomStyle: '',
    hasToggle: false,
    toggleValue: false,
    isLink: true,
    routerLinks: [],
  };

  private readonly archiveAction: ActionList = {
    key: 'archive',
    hasIcon: true,
    label: this.transloco.translate('archive_action_item_label'),
    hasDivider: false,
    labelCustomStyle: 'text-destructive-500 text-xs',
    hasToggle: false,
    toggleValue: false,
  };

  private readonly restoreAction: ActionList = {
    key: 'restore',
    hasIcon: true,
    label: this.transloco.translate('restore_action_item_label'),
    hasDivider: false,
    labelCustomStyle: '',
    hasToggle: false,
    toggleValue: false,
  };

  filterOptions: FilterOption[] = FILTER_OPTIONS.map(option => ({
    ...option,
    label: this.transloco.translate(option.label),
    placeholder: option.placeholder
      ? this.transloco.translate(option.placeholder)
      : '',
  }));

  readonly filterMapping = HISTORY_FILTER_MAPPING;

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        this.requestId.set(+(params['id'] ?? params['requestId']));
      });
  }

  checkReturnedObservable = (): Observable<ApiResponse<RequestHistory>> => {
    this.isLoading.set(true);
    return this.activeTableFilterTab().key === this.tabKeys.ARCHIVED
      ? this.getArchiveRequests$()
      : this.getHistoryData$();
  };

  handleHistoryStreamSuccess(res: ApiResponse<RequestHistory>) {
    this.isLoading.set(false);
    this.historyData.set(
      res.data.map(element => {
        const row: RequestHistory = {
          ...element,
          assignees: element.current_assignees.map(assignee => assignee.data),
          assigneeType: element.current_assignees.map(
            assignee => assignee.type
          ),
          permissions: this.permissions(),
        };
        return { ...row, actionList: this.buildActionList(row) };
      })
    );
    this.meta.set(res.meta);
  }

  handleApiCall() {
    this.checkReturnedObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => this.handleHistoryStreamSuccess(res),
        error: () => this.isLoading.set(false),
      });
  }

  private getHistoryData$(): Observable<ApiResponse<RequestHistory>> {
    const { page, size, search, sortColumn } = this.payload();
    let { filters } = this.payload();

    if (this.additionalFilters()) {
      filters = { ...filters, ...this.additionalFilters() };
    }

    return this.requestService.getRequestHistory(
      this.requestId().toString(),
      page,
      size,
      this.activeTableFilterTab().key,
      sortColumn,
      search,
      filters
    );
  }

  private getArchiveRequests$(): Observable<ApiResponse<RequestHistory>> {
    const { page, size, search, sortColumn } = this.payload();
    let { filters } = this.payload();

    if (this.additionalFilters()) {
      filters = { ...filters, ...this.additionalFilters() };
    }

    return this.myInboxService.getFilteredArchive(
      page,
      size,
      HISTORY_ARCHIVE_REQUEST_TYPE,
      sortColumn,
      0,
      this.requestId(),
      search,
      filters
    );
  }

  private buildActionList(row: RequestHistory): ActionList[] {
    if (this.activeTableFilterTab().key === this.tabKeys.IN_PROGRESS) {
      return [
        {
          ...this.viewDetailsAction,
          routerLinks: [
            this.tenantService.tenantPath(
              `${EMPLOYEE_ROUTES.MY_INBOX.REQUEST}/${RequestTypeEnum.VIEW}/${row.id}`
            ),
          ],
        },
      ];
    }

    const viewAction: ActionList = {
      ...this.viewDetailsAction,
      routerLinks: [
        this.tenantService.tenantPath(
          `${EMPLOYEE_ROUTES.REQUESTS.REQUEST}/${RequestTypeEnum.VIEW}/${row.id}`
        ),
      ],
    };

    if (
      (row.state.value === StepStatusEnum.COMPLETED ||
        row.state.value === StepStatusEnum.REJECTED) &&
      this.permissionsService.checkPermission(
        [this.appPermissions.workflow.archive],
        row.permissions as any
      )
    ) {
      return [
        viewAction,
        this.activeTableFilterTab().key === this.tabKeys.ARCHIVED
          ? this.restoreAction
          : this.archiveAction,
      ];
    }

    if (
      this.permissionsService.checkPermission(
        [this.appPermissions.workflow.view],
        row.permissions as any
      )
    ) {
      return [viewAction];
    }

    return [];
  }

  actionListItemClick(option: any, request: any) {
    this.selectedRequestId.set(request.id);
    switch (option) {
      case 'archive':
        this.openModal('archive');
        break;

      case 'restore':
        this.openModal('restore');
        break;

      case 'view_details':
        break;
    }
  }

  openModal(type: string) {
    let modalType;
    switch (type) {
      case 'archive':
        modalType = this.archiveModal();
        break;
      case 'restore':
        modalType = this.restoreModal();
        break;
    }
    const modal = {
      isOpen: true,
      hasCloseIcon: false,
      customizeContent: modalType,
      width: '24',
    };

    this.modalService.modalContent.set(modal);
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  archiveRequest() {
    this.closeModal();
    this.isLoading.set(true);
    this.myInboxService
      .archiveRequest(this.selectedRequestId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.handleApiCall();
        },
        error: (error: any) => {
          this.toasterService.add(
            error.error.message,
            '',
            '',
            'error',
            true,
            true
          );
          this.isLoading.set(false);
        },
      });
  }

  restoreRequest() {
    this.closeModal();
    this.isLoading.set(true);
    this.myInboxService
      .restoreRequest(this.selectedRequestId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.handleApiCall();
        },
        error: (error: any) => {
          this.toasterService.add(
            error.error.message,
            '',
            '',
            'error',
            true,
            true
          );
          this.isLoading.set(false);
        },
      });
  }

  submitExportForm(form: FormGroup) {
    let formValue = form.value;

    this.requestService
      .exportWorkflow(formValue, this.requestId(), this.additionalFilters())
      .subscribe({
        next: (response: any) => {
          this.exportService.handleExportSuccess(response, form, () =>
            this.closeModal()
          );
        },
        error: (error: any) => {
          this.toasterService.add(
            error.error?.message,
            '',
            '',
            'error',
            true,
            true
          );
          this.exportService.reInitExportForm(form);
          this.closeModal();
        },
      });
  }
}
